/**
 * Public referral API client.
 *
 * Handles the visitor-facing (un-authenticated) side of the referral funnel:
 *   1. trackClick      — record a landing from ?ref=CODE, ?code=CODE, or /r/:slug
 *   2. resolveLinkSlug — exchange a /r/:slug for the destination URL
 *   3. validateCode    — compute a discount preview before checkout
 *   4. local storage   — persist attribution across navigations and sessions
 *
 * Session identity is a UUID stored in localStorage under `visitor_session_id`
 * so that repeat visits from the same browser stitch together.
 */
import { getApiBaseUrl } from '@/lib/api'

const PROJECT_KEY = 'icc'

const ATTRIBUTION_STORAGE_KEY = 'referral_attribution'
const SESSION_STORAGE_KEY = 'visitor_session_id'

// ─── Types ────────────────────────────────────────────────────────────

export interface ReferralAttribution {
  code?: string | null
  slug?: string | null
  influencerName?: string | null
  studentDiscountPercent?: number | null
  expiresAt: string // ISO-8601
}

export interface TrackClickResponse {
  referralCodeId: string
  influencerName: string
  studentDiscountPercent: number | null
  code: string | null
  slug: string | null
  attributionExpiresAt: string
}

export interface ValidateCodeResponse {
  codeValid: boolean          // backend uses codeValid
  code: string
  influencerName?: string
  studentDiscountPercent?: number
  originalAmountPaise?: number | null
  discountAmountPaise?: number | null // backend field name
  finalAmountPaise?: number | null
  message?: string            // backend populates on both success and failure
  referralCodeId?: string
}

export interface ResolveLinkResponse {
  redirectUrl: string
  referralCodeId: string
  influencerName: string
  studentDiscountPercent: number | null
  code: string | null
  slug: string
  attributionExpiresAt: string
}

// ─── Session id ───────────────────────────────────────────────────────

function generateUuid(): string {
  // Prefer the native crypto.randomUUID when available (secure contexts)
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback: RFC4122-ish v4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function getOrCreateSessionId(): string {
  try {
    let id = localStorage.getItem(SESSION_STORAGE_KEY)
    if (!id) {
      id = generateUuid()
      localStorage.setItem(SESSION_STORAGE_KEY, id)
    }
    return id
  } catch {
    // localStorage unavailable (private window, disabled storage) — fall back to
    // a per-page UUID. Attribution won't stick across navigations but the request
    // will still be accepted.
    return generateUuid()
  }
}

// ─── Attribution storage ──────────────────────────────────────────────

/** Reads stored attribution, returning `null` if missing or expired. */
export function getAttributionFromStorage(): ReferralAttribution | null {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ReferralAttribution
    if (!parsed || !parsed.expiresAt) return null
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      // Expired — clean up
      localStorage.removeItem(ATTRIBUTION_STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function saveAttributionToStorage(attr: ReferralAttribution): void {
  try {
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attr))
  } catch {
    // storage unavailable — attribution simply won't persist
  }
}

export function clearAttribution(): void {
  try {
    localStorage.removeItem(ATTRIBUTION_STORAGE_KEY)
  } catch {
    // no-op
  }
}

// ─── Network helpers ──────────────────────────────────────────────────

interface ApiEnvelope<T> {
  success: boolean
  statusCode: number
  message: string
  data: T
}

async function publicFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Project-Key': PROJECT_KEY,
    ...(init.headers as Record<string, string> | undefined),
  }
  const res = await fetch(`${getApiBaseUrl()}${path}`, { ...init, headers })
  const json = (await res.json()) as ApiEnvelope<T>
  if (!json.success) {
    throw new Error(json.message || 'Referral request failed')
  }
  return json.data
}

// ─── Public endpoints ─────────────────────────────────────────────────

/**
 * Records a click/landing event. Non-blocking by design: the caller should
 * fire-and-forget this so page rendering is never gated by analytics.
 */
export async function trackClick(codeOrSlug: string): Promise<TrackClickResponse> {
  const sessionId = getOrCreateSessionId()
  const payload = {
    codeOrSlug,
    visitorSessionId: sessionId,
    landingPath: typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/',
    referrerUrl: typeof document !== 'undefined' ? document.referrer || null : null,
  }
  return publicFetch<TrackClickResponse>('/api/icc/referral/track-click', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

/**
 * Resolves a `/r/:slug` link — the backend records attribution and tells us
 * where to redirect (e.g. `/courses` or a specific course landing page).
 */
export async function resolveLinkSlug(slug: string): Promise<ResolveLinkResponse> {
  const sessionId = getOrCreateSessionId()
  const qs = new URLSearchParams({ visitorSessionId: sessionId })
  return publicFetch<ResolveLinkResponse>(`/api/icc/referral/link/${encodeURIComponent(slug)}?${qs.toString()}`)
}

/**
 * Validates a referral code and returns the discount breakdown so the UI can
 * preview the final price before the user commits to checkout.
 */
export async function validateCode(
  code: string,
  amountPaise: number,
  _courseId?: string,
): Promise<ValidateCodeResponse> {
  return publicFetch<ValidateCodeResponse>('/api/icc/referral/validate-code', {
    method: 'POST',
    body: JSON.stringify({ code, originalAmountPaise: amountPaise }),
  })
}

export const referralClientApi = {
  trackClick,
  resolveLinkSlug,
  validateCode,
  getAttributionFromStorage,
  saveAttributionToStorage,
  clearAttribution,
  getOrCreateSessionId,
}

export default referralClientApi
